import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api";

export type Notice = {
  id: string;
  type: string;
  title: string;
  detail: string | null;
  category: string;
  href: string | null;
  order_code: string | null;
  created_at: string;
  read_at: string | null;
  done_at: string | null;
  done_by: string | null;
  done_by_name: string | null;
  deleted_at: string | null;
  can_delete: boolean;
  can_mark_done: boolean;
  level: "urgent" | "normal" | "summary";
};

export type NoticeList = {
  items: Notice[];
  unread_count: number;
  category_counts: Record<string, number>;
};

export type NoticeFolder = "inbox" | "done" | "trash";
export type NoticeAction = "read" | "unread" | "done" | "trash" | "restore";

export type NoticePolicy = {
  type: string;
  label: string;
  category: string;
  level: "urgent" | "normal" | "summary";
  channel: "bell" | "alerts_only";
  role_codes: string[];
  workday_start: string;
  workday_end: string;
  after_hours: boolean;
  can_delete: boolean;
  grouped: boolean;
  parameters: Record<string, unknown>;
  activated_at?: string;
  updated_at?: string;
};

export type PushDevice = { id: string; device_name: string; created_at: string; last_seen_at: string | null };

const noticeKey = ["notifications"] as const;

export function useNotices(folder: NoticeFolder, category: string, search: string) {
  const query = new URLSearchParams({ folder });
  if (category) query.set("category", category);
  if (search.trim()) query.set("search", search.trim());
  return useQuery({
    queryKey: [...noticeKey, folder, category, search],
    queryFn: () => apiGet<NoticeList>(`/notifications?${query}`),
    refetchInterval: 30_000,
  });
}

export function useNoticeCount(enabled = true) {
  return useQuery({
    queryKey: [...noticeKey, "count"],
    queryFn: () => apiGet<{ unread_count: number }>("/notifications/count"),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useNoticeAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: NoticeAction }) =>
      apiPost<Notice>(`/notifications/${id}/${action}`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: noticeKey }),
  });
}

export function useReadAllNotices() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ updated: number }>("/notifications/read-all", {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: noticeKey }),
  });
}

export function useNoticePolicies(enabled = true) {
  return useQuery({ queryKey: ["notification-policies"], queryFn: () => apiGet<NoticePolicy[]>("/notification-policies"), enabled });
}

export function useSaveNoticePolicy() {
  const client = useQueryClient();
  return useMutation({
    // Solo lo editable (PolicyUpdate): la API rechaza cualquier otro campo de la fila, como el
    // tipo (va en la ruta), el nombre, la categoría o sus fechas.
    mutationFn: (p: NoticePolicy) =>
      apiPut<NoticePolicy>(`/notification-policies/${p.type}`, {
        level: p.level, channel: p.channel, role_codes: p.role_codes,
        workday_start: p.workday_start, workday_end: p.workday_end, after_hours: p.after_hours,
        can_delete: p.can_delete, grouped: p.grouped, parameters: p.parameters,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["notification-policies"] }),
  });
}

export function useTestNotice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ id: string; title: string; href: string | null }>("/notifications/test", {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: noticeKey }),
  });
}

export function usePushDevices(enabled = true) {
  return useQuery({ queryKey: ["push-subscriptions"], queryFn: () => apiGet<PushDevice[]>("/push-subscriptions"), enabled });
}

export function usePushPublicKey() {
  return useQuery({ queryKey: ["push-subscriptions", "public-key"], queryFn: () => apiGet<{ public_key: string }>("/push-subscriptions/vapid-public-key"), retry: false });
}

export function useSavePushDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { endpoint: string; keys: { p256dh: string; auth: string }; device_name: string }) =>
      apiPost<PushDevice>("/push-subscriptions", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["push-subscriptions"] }),
  });
}

export function useRemovePushDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/push-subscriptions/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["push-subscriptions"] }),
  });
}
